import { useLocation } from 'react-router-dom';
import { X, ShoppingBag } from 'lucide-react';
import { useCart } from '@/contexts/CartContext';
import { useStripeCheckout } from '@/hooks/useStripeCheckout';
import { isPaymentsConfigured } from '@/lib/stripe';
import { BUNDLE_PRICE, PAIRINGS, workbooks } from '@/data/workbooks';
import { BookCover } from '@/components/workbooks/BookCover';
import { TrustRow } from '@/components/workbooks/TrustRow';
import { track } from '@/lib/funnel';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';

/**
 * Persistent cart summary. Mounted once globally so a reader can collect
 * workbooks from articles and check out from anywhere.
 */
export function CartBar() {
  const cart = useCart();
  const location = useLocation();
  const { openCheckout, closeCheckout, isOpen, checkoutTitle, checkoutElement } =
    useStripeCheckout();

  // Never overlay the payment or delivery pages.
  const hidden =
    location.pathname.startsWith('/purchase-success') ||
    location.pathname.startsWith('/library') ||
    location.pathname.startsWith('/admin');

  if (hidden || cart.count === 0) return null;

  const startCheckout = () => {
    if (!isPaymentsConfigured()) return;
    track('begin_checkout', { value: cart.total, items: cart.count });
    cart.setOpen(false);
    openCheckout({
      priceIds: cart.priceIds,
      title: cart.isBundle
        ? 'The Complete Workbook Library'
        : `${cart.count} workbook${cart.count > 1 ? 's' : ''}`,
    });
  };

  // Titles not yet in the cart, the natural companion of the last added first.
  const lastAdded = cart.items[cart.items.length - 1];
  const companionSlug = lastAdded ? PAIRINGS[lastAdded.slug] : undefined;
  const suggestions = workbooks
    .filter((w) => !cart.has(w.slug))
    .sort((a, b) =>
      a.slug === companionSlug ? -1 : b.slug === companionSlug ? 1 : 0,
    )
    .slice(0, 3);

  // What the cart would cost after adding one more title.
  const oneMore = suggestions[0];
  const nextTotal =
    oneMore && cart.nextTier?.needed === 1
      ? Math.round(
          (cart.subtotal + oneMore.price) * (1 - cart.nextTier.percentOff / 100),
        )
      : null;

  // With five or six titles the complete library is often cheaper outright.
  const remaining = workbooks.length - cart.count;
  const bundleGap =
    !cart.isBundle && cart.count >= 5 ? cart.total - BUNDLE_PRICE : null;


  return (
    <>
      {/* Keeps the fixed bar from covering the footer */}
      <div aria-hidden className="h-24" />
      <div className="luxe-ink fixed inset-x-0 bottom-0 z-40 shadow-luxe-hover">
        <div className="foil-rule absolute inset-x-0 top-0" />
        <div className="mx-auto flex max-w-5xl flex-col gap-2 px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
          <button
            type="button"
            onClick={() => cart.setOpen(true)}
            className="flex items-center gap-3 text-left"
          >
            <ShoppingBag className="h-4 w-4 text-foil" aria-hidden />
            <span className="text-sm font-light text-ink-surface-foreground">
              <span className="font-medium">
                {cart.count} {cart.count === 1 ? 'workbook' : 'workbooks'}
              </span>
              <span className="mx-2 text-ink-surface-foreground/40">·</span>
              {cart.saving > 0 && (
                <span className="mr-2 text-ink-surface-foreground/50 line-through">
                  ${cart.subtotal}
                </span>
              )}
              <span className="font-display text-lg">${cart.total}</span>
              {cart.saving > 0 && (
                <span className="ml-2 text-foil">save ${cart.saving}</span>
              )}
            </span>
          </button>

          <div className="flex items-center gap-4">
            {cart.nextTier && (
              <span className="foil-label hidden sm:block">
                Add {cart.nextTier.needed} more to save {cart.nextTier.percentOff}%
              </span>
            )}
            <button
              type="button"
              onClick={() => cart.setOpen(true)}
              className="text-[10px] font-light uppercase tracking-luxury text-ink-surface-foreground/70 underline-offset-4 hover:text-ink-surface-foreground hover:underline"
            >
              Review
            </button>
            <button
              type="button"
              onClick={startCheckout}
              className="btn-luxe rounded-none px-6 py-3 text-[10px] font-medium uppercase tracking-luxury"
            >
              Checkout
            </button>
          </div>
        </div>
        {cart.nextTier && (
          <p className="foil-label border-t border-ink-surface-foreground/10 px-4 py-2 text-center sm:hidden">
            Add {cart.nextTier.needed} more to save {cart.nextTier.percentOff}%
          </p>
        )}
      </div>


      <Sheet
        open={cart.isOpen}
        onOpenChange={(open) => {
          if (open) track('view_cart', { value: cart.total, items: cart.count });
          cart.setOpen(open);
        }}
      >
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle className="font-serif text-xl font-medium">
              Your workbooks
            </SheetTitle>
            <SheetDescription className="text-sm font-light">
              Two titles save 10%, three save 15%, four or more save 20%. All
              seven are charged as the complete library at ${BUNDLE_PRICE}.
            </SheetDescription>
          </SheetHeader>

          <ul className="mt-6 divide-y divide-border">
            {cart.items.map((item) => (
              <li key={item.slug} className="flex items-start justify-between gap-4 py-3">
                <div>
                  <p className="text-sm font-medium">{item.title}</p>
                  <p className="text-xs font-light text-muted-foreground">
                    {item.pages} pages · ${item.price}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => cart.remove(item.slug)}
                  aria-label={`Remove ${item.title}`}
                  className="text-muted-foreground transition-colors hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>

          {suggestions.length > 0 && (
            <div className="mt-6 border-t border-border pt-4">
              <p className="text-[10px] uppercase tracking-luxury text-muted-foreground">
                Often worked through together
              </p>
              <ul className="mt-3 space-y-3">
                {suggestions.map((w) => (
                  <li key={w.slug} className="flex items-center gap-3">
                    <BookCover title={w.title} tone={w.coverTone} size="sm" hover={false} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium leading-snug">{w.title}</p>
                      <p className="text-xs font-light text-muted-foreground">
                        {w.pages} pages · ${w.price}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => cart.add(w.slug)}
                      className="btn-luxe-ghost shrink-0 px-3 py-2 text-[10px] uppercase tracking-luxury"
                    >
                      Add
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}



          <div className="mt-6 space-y-1 pt-4 text-sm font-light">
            <div className="foil-rule mb-4" />
            <div className="flex justify-between">
              <span className="text-muted-foreground">Subtotal</span>
              <span>${cart.subtotal}</span>
            </div>
            {cart.saving > 0 && (
              <div className="flex justify-between text-muted-foreground">
                <span>
                  {cart.isBundle
                    ? 'Complete library price'
                    : `Volume discount (${cart.percentOff}%)`}
                </span>
                <span>-${cart.saving}</span>
              </div>
            )}
            <div className="flex items-baseline justify-between pt-2">
              <span className="text-[10px] uppercase tracking-luxury text-muted-foreground">
                Total
              </span>
              <span className="font-display text-3xl text-foreground">${cart.total}</span>
            </div>
          </div>

          {bundleGap !== null && bundleGap > 0 && oneMore ? (
            <div className="luxe-card mt-4 p-4">
              <p className="text-xs font-light leading-relaxed text-foreground">
                Adding the last {remaining === 1 ? 'title' : `${remaining} titles`} charges
                the complete library at ${BUNDLE_PRICE} — ${bundleGap} less than your
                cart right now.
              </p>
              <button
                type="button"
                onClick={() => workbooks.forEach((w) => cart.add(w.slug))}
                className="mt-3 text-[10px] uppercase tracking-luxury text-brass-deep underline-offset-4 hover:underline"
              >
                Take all seven for ${BUNDLE_PRICE}
              </button>
            </div>
          ) : (
            !cart.isBundle &&
            cart.nextTier && (
              <p className="mt-3 text-xs font-light text-muted-foreground">
                {cart.nextTier.needed === 1 && nextTotal !== null
                  ? `Add one more title and the whole cart drops ${cart.nextTier.percentOff}% — $${nextTotal} instead of $${cart.total + (oneMore?.price ?? 0)}.`
                  : `Add ${cart.nextTier.needed} more titles to reach ${cart.nextTier.percentOff}% off.`}
              </p>
            )
          )}


          <button
            type="button"
            onClick={startCheckout}
            className="btn-luxe mt-6 w-full rounded-none px-6 py-3.5 text-[10px] font-medium uppercase tracking-luxury"
          >
            Pay ${cart.total}
          </button>

          <TrustRow className="mt-5 border-t border-border pt-4" />

          <button
            type="button"
            onClick={cart.clear}
            className="mt-3 w-full text-xs font-light uppercase tracking-wider text-muted-foreground underline-offset-4 hover:underline"
          >
            Empty cart
          </button>
        </SheetContent>
      </Sheet>

      <Dialog open={isOpen} onOpenChange={(open) => !open && closeCheckout()}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl font-medium">
              {checkoutTitle}
            </DialogTitle>
            <DialogDescription className="text-sm font-light">
              Any multi-title discount is already applied to the total below.
              Downloads appear as soon as payment clears, together with a private
              library link.
            </DialogDescription>
          </DialogHeader>
          <p className="border-y border-border/50 py-3 text-xs font-light text-muted-foreground">
            {cart.count} workbook{cart.count === 1 ? '' : 's'}
            {cart.isBundle
              ? ' · complete library price'
              : cart.percentOff > 0
                ? ` · ${cart.percentOff}% applied`
                : ''}
            {cart.saving > 0 ? ` · you save $${cart.saving}` : ''}
          </p>
          {checkoutElement}

        </DialogContent>
      </Dialog>
    </>
  );
}
